import { useCallback, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';

import { FiltroFechaContext, type RangoFecha } from './context';

const STORAGE_KEY = 'asi.filtroFecha';

interface Props {
  children: ReactNode;
}

/** dayjs no es serializable — se persiste como par de strings ISO
 * (YYYY-MM-DD) o null, mismo criterio de "no reventar si localStorage no
 * está disponible" que EmpresaProvider. */
function readStoredRango(): RangoFecha {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const [desde, hasta] = JSON.parse(raw) as [string | null, string | null];
    return [desde ? dayjs(desde) : null, hasta ? dayjs(hasta) : null];
  } catch {
    return null;
  }
}

export function FiltroFechaProvider({ children }: Props) {
  const [rango, setRangoState] = useState<RangoFecha>(readStoredRango);

  const setRango = useCallback((next: RangoFecha) => {
    setRangoState(next);
    try {
      if (!next || (!next[0] && !next[1])) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([
          next[0] ? next[0].format('YYYY-MM-DD') : null,
          next[1] ? next[1].format('YYYY-MM-DD') : null,
        ]));
      }
    } catch {
      // Nada que hacer si localStorage no está disponible — la selección
      // simplemente no persiste entre sesiones.
    }
  }, []);

  return (
    <FiltroFechaContext.Provider value={{ rango, setRango }}>
      {children}
    </FiltroFechaContext.Provider>
  );
}
